/**
 * Loss Runs types — FE-facing rows + the v2 wire shape.
 *
 * Wired to the real backend (jun-16): `/v2/submissions/{id}/loss-runs/`.
 * The FE keeps camelCase string fields (editable cells round-trip the raw
 * value; money/date masking happens at the cell). Server-owned values
 * (`sirByCommunity`, `totalIncurred`) are read-only display columns — NOT
 * writable.
 * The `location` cell shows the schedule location's name; the adapter
 * maps it to/from `location_id` (FK) using the submission's locations.
 *
 * Display columns the backend does NOT expose per row (shown as "—",
 * never computed FE-side — backend-prevails, jun-16):
 *   - Loss Excess the Retention (per row)
 *   - Accident Year / Report Year
 *   Card totals come from the list-response aggregates (LossRunAggregates);
 *   the Annual Loss Summary + Loss Ratio Analysis tabs come from the
 *   `/loss-runs/summary` endpoint (LossRunSummary).
 */

/** Claim lifecycle status — drives the inline Status dropdown. */
export type LossRunStatus = "" | "Open" | "Closed" | "Re-Open" | "Archived";

/**
 * Marks a claim as archived (OR-1431).
 *
 * ⚠️ This rides on `status` because the backend has no per-claim exclusion
 * flag: `submission_loss_run` accepts 18 fields and none of them is one, while
 * `is_excluded` lives only on `SubmissionLocation`. `status` is the only
 * writable field that survives a round-trip, so it stands in until the backend
 * grows the real thing.
 *
 * Two consequences worth knowing: it OVERWRITES the carrier's real status (the
 * previous value is remembered for the session only — see `claim-archive.ts`),
 * and the backend-derived Loss Ratio card and generated documents still count
 * an archived claim — no calculation there reads this field. The two FE-derived
 * cards and the table footer DO honor it (`isExcludedFromLossRunTotals` in
 * `claim-archive.ts`).
 */
export const ARCHIVED_CLAIM_STATUS = "Archived" as const;

/** The dropdown's options. `Archived` is deliberately absent: it isn't a claim
 *  lifecycle state the user picks, it's set by the row's ⋮ menu. */
export const LOSS_RUN_STATUSES: readonly Exclude<LossRunStatus, "" | "Archived">[] = ["Open", "Closed", "Re-Open"];

export interface LossRunRow {
    id: string;
    submissionId: string;

    // ── Claimant / claim ─────────────────────────────────────
    claimantName: string;
    claimNumber: string;

    // ── Context ──────────────────────────────────────────────
    operator: string;
    /** Schedule location name (display + off-schedule warning). */
    location: string;
    /** Schedule location id — the wire `location_id` FK. Canonical join
     *  handle: schedule names can be duplicated (OR-1409/OR-1417), so the
     *  id, not the name, decides which location a row points at. "" = none. */
    locationId: string;
    /** Legal venue — 2-letter US state code. Editable. */
    venue: string;
    descriptionOfLoss: string;

    // ── Dates (MM/DD/YYYY) ───────────────────────────────────
    incidentDate: string;
    reportDate: string;

    // ── Amounts (numeric strings, money-masked at the cell) ──
    paidIndemnity: string;
    paidExpense: string;
    reservedIndemnity: string;
    reservedExpense: string;

    // ── Status / policy ──────────────────────────────────────
    status: LossRunStatus;
    carrier: string;
    /** Valuation date (MM/DD/YYYY). */
    valued: string;

    // ── Server-owned, read-only display ──────────────────────
    /** SIR by Community — the backend's `sir_by_community`: the matched
     *  location's SIR (Schedule "Retention") resolved via `location_id`.
     *  Distinct from the wire's `sir` (retention at the time of the loss),
     *  which has no UI column. */
    sirByCommunity: string;
    /** Total Paid & Reserved — the backend's per-row `total_incurred`. */
    totalIncurred: string;

    // ── Server metadata ──────────────────────────────────────
    createdAt: string;
    updatedAt: string;
}

/** Editable subset (write payload). Excludes server-managed fields AND
 *  the read-only server-owned columns (`sirByCommunity`, `totalIncurred`,
 *  and `operator` — derived from the linked location, OR-1370/OR-1371). */
export type LossRunWritePayload = Partial<
    Omit<LossRunRow, "id" | "submissionId" | "createdAt" | "updatedAt" | "sirByCommunity" | "totalIncurred" | "operator">
>;

/** What a consumer may stage into working state via `update`: the write
 *  payload PLUS `operator`. Operator stays read-only on the write path (it's
 *  never sent — see `WRITABLE_KEYS`), but the Location cell mirrors the linked
 *  facility's operator optimistically so the display isn't stale until
 *  save + reload (OR-1411). */
export type LossRunStagePayload = LossRunWritePayload & Partial<Pick<LossRunRow, "operator">>;

/** Card totals over ALL rows, computed by the backend (list-response).
 *  Decimal-as-string; the FE formats them. */
export interface LossRunAggregates {
    /** Total Paid & Reserved card. */
    totalPaidAndReserved: string;
    /** Loss Excess the Retention card. */
    lossExcessSir: string;
    /** "Loss Ratio Adjusted for Retention" card — rendered as the backend
     *  returns it (see orion-rewiring-questions.md: $ vs % is flagged). */
    sirAdjustedLossRatio: string;
}

// ───────────────────── Summary (Annual + Ratio tabs) ──────────────────
//
// The `/loss-runs/summary` endpoint returns one breakdown row per report
// year, feeding BOTH the Annual Loss Summary tab and the Loss Ratio
// Analysis tab. Read-only — the FE never computes these (backend-prevails).
// Money/ratio values are Decimal-as-string; "" means the backend returned
// null (rendered "—"). Counts are numbers (null → "—").

export interface LossRunSummaryRow {
    /** Policy / report year bucket. */
    reportYear: number;

    // ── Annual Loss Summary columns ──────────────────────────
    indemnityIncurred: string;
    expenseIncurred: string;
    reservedIndemnity: string;
    reservedExpenses: string;
    /** Total Incurred (sum the backend already computed). */
    incurred: string;
    incurredClaimsCount: number | null;
    reportedIncidentsCount: number | null;
    incurredPriorYear: string;
    /** % Change YOY — a ratio (0.95 → 95%). */
    incurredPercentChange: string;

    // ── Loss Ratio Analysis columns ──────────────────────────
    /** Scaled skilled-equivalent bed count for the year (OR-1117). */
    equivalency: string;
    /** Premium (on-leveled). */
    onLeveledPremium: string;
    /** Loss Ratio — ratio (0.43 → 43%). */
    onLeveledLossRatio: string;
    selectedPremium: string;
    /** Adjusted XS the SIR Chosen. */
    adjustedExcess: string;
    /** Adjusted Loss Ratio Xs the SIR — ratio. */
    adjustedLossRatio: string;
}

export interface LossRunSummary {
    /** Footer/aggregate totals the backend computes over all years. */
    totalPaidAndReserved: string;
    totalExcess: string;
    /** Ratio (0–1) or "" when null. */
    totalAdjustedLossRatio: string;
    // ── New summary totals (OR-1117) ─────────────────────────
    /** Distinct report years with losses. */
    yearsWithLosses: number | null;
    /** Total scaled bed count across locations. */
    totalEquivalency: string;
    /** Σ schedule unadjusted_premium ÷ total_equivalency ("" when zero). */
    unadjustedRatePerBed: string;
    /** New-policy annual premium. */
    premium: string;
    /** premium × years_with_losses. */
    totalPremium: string;
    rows: LossRunSummaryRow[];
}

// ───────────────────────────── Wire ───────────────────────────────

export interface LossRunWire {
    id: string;
    submission_id: string;
    location_id: string | null;
    claimant: string | null;
    claim_number: string | null;
    operator_name: string | null;
    description: string | null;
    incident_date: string | null;
    report_date: string | null;
    indemnity_paid: string | null;
    expense_paid: string | null;
    indemnity_reserved: string | null;
    expense_reserved: string | null;
    total_incurred: string | null;
    status: string | null;
    carrier_name: string | null;
    valuation_date: string | null;
    sir: string | null;
    sir_by_community: string | null;
    venue: string | null;
    reopened_date: string | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
}

export interface LossRunListResponseWire {
    total: number;
    items: LossRunWire[];
    total_paid: string;
    total_reserved: string;
    total_paid_and_reserved: string;
    loss_excess_sir: string | null;
    sir_adjusted_loss_ratio: string | null;
}

/** `/loss-runs/summary` — one breakdown row per report year. */
export interface LossRunSummaryBreakdownWire {
    report_year: number;
    indemnity_incurred: string | null;
    expense_incurred: string | null;
    reserved_indemnity: string | null;
    reserved_expenses: string | null;
    incurred: string | null;
    incurred_claims_count: number | null;
    reported_incidents_count: number | null;
    incurred_prior_year: string | null;
    incurred_percent_change: string | null;
    /** Loss Ratio Analysis columns (OR-1117 — now populated by the backend). */
    equivalency: string | null;
    on_leveled_premium: string | null;
    on_leveled_loss_ratio: string | null;
    selected_premium: string | null;
    adjusted_excess: string | null;
    adjusted_loss_ratio: string | null;
}

export interface LossRunSummaryWire {
    total_paid_and_reserved: string | null;
    total_excess: string | null;
    total_adjusted_loss_ratio: string | null;
    // New summary totals (OR-1117).
    years_with_losses: number | null;
    total_equivalency: string | null;
    unadjusted_rate_per_bed: string | null;
    premium: string | null;
    total_premium: string | null;
    breakdown: LossRunSummaryBreakdownWire[];
}

/** PATCH/POST body — every key optional so a single-cell PATCH is minimal. */
export type LossRunWriteWire = Partial<{
    location_id: string | null;
    claimant: string | null;
    claim_number: string | null;
    // operator_name removed from the write wire (OR-1370/OR-1371): backend
    // derives it from location_id and ignores it on create/update.
    description: string | null;
    incident_date: string | null;
    report_date: string | null;
    indemnity_paid: string | null;
    expense_paid: string | null;
    indemnity_reserved: string | null;
    expense_reserved: string | null;
    status: string | null;
    carrier_name: string | null;
    valuation_date: string | null;
    venue: string | null;
}>;

/** Factory for a brand-new loss run with every editable field blank.
 *  Enumerates exactly the `LossRunWritePayload` keys (canonical writable
 *  list for `use-loss-runs.ts`). */
export function emptyLossRun(): LossRunWritePayload {
    return {
        claimantName: "",
        claimNumber: "",
        location: "",
        locationId: "",
        venue: "",
        descriptionOfLoss: "",
        incidentDate: "",
        reportDate: "",
        paidIndemnity: "",
        paidExpense: "",
        reservedIndemnity: "",
        reservedExpense: "",
        status: "",
        carrier: "",
        valued: "",
    };
}
